import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { Stack } from '@mui/material';
import { useFactoringRequestsReceived } from 'lib/api/factoring';
import {
  formatDateString,
  formatMoney,
  getShortString,
} from 'lib/functions/utils';
import {
  BottomDock,
  BoxItemStack,
  ContentCard,
  FilterDock,
  FilterGroup,
  type FilterPillOption,
  FilterPills,
  getFactoringStatusChipType,
  getFactoringStatusLabel,
  PaginationDock,
} from 'lib/mobileDesignSystem';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 10;
const DEFAULT_STATUS_VALUES = [
  'CREATED',
  'IN_REVIEW',
  'ACCEPTED',
  'IN_TRANSFER',
];

const STATUS_OPTIONS: FilterPillOption[] = [
  { label: 'Creada', value: 'CREATED' },
  { label: 'En revisión', value: 'IN_REVIEW' },
  { label: 'Aceptada', value: 'ACCEPTED' },
  { label: 'Rechazada', value: 'REJECTED' },
  { label: 'En giro', value: 'IN_TRANSFER' },
  { label: 'Activa', value: 'ACTIVE' },
  { label: 'Completada', value: 'COMPLETED' },
];

const FactoringRequests: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [appliedStatuses, setAppliedStatuses] = useState<string[]>(
    DEFAULT_STATUS_VALUES,
  );
  const [searchStatuses, setSearchStatuses] = useState<FilterPillOption[]>([
    { label: 'Creada', value: 'CREATED' },
    { label: 'En revisión', value: 'IN_REVIEW' },
    { label: 'Aceptada', value: 'ACCEPTED' },
    { label: 'En giro', value: 'IN_TRANSFER' },
  ]);

  const {
    data: requests = {
      data: [],
      pagination: { page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 0 },
    },
    isLoading,
    isFetching,
  } = useFactoringRequestsReceived(dispatch, {
    page,
    pageSize: PAGE_SIZE,
    statuses: appliedStatuses,
  });

  const handleSearch = useCallback(() => {
    setAppliedStatuses(searchStatuses.map((s) => s.value));
    setPage(1);
  }, [searchStatuses]);

  const itemsWithNavigation = requests.data.map((item) => ({
    primaryText: getShortString(item.businessName, 20),
    amount: formatMoney(item.amount),
    secondaryText:
      formatDateString(item.createdAt) + ' · ' + item.recipientName,
    chipText: getFactoringStatusLabel(item.status),
    chipType: getFactoringStatusChipType(item.status),
    onClick: () => navigate(`/finance/factoring/${item.id}`),
  }));

  return (
    <Stack spacing={2} sx={{ pb: 10 }}>
      <ContentCard
        header={{
          icon: <ManageSearchIcon sx={{ fontSize: 20 }} />,
          title: 'Solicitudes de factoring',
          description: 'Gestiona tus solicitudes de factoring.',
        }}
      >
        <BoxItemStack
          items={itemsWithNavigation}
          loading={isLoading}
          skeletonCount={10}
        />
      </ContentCard>
      <BottomDock
        center={
          <PaginationDock
            page={page}
            totalPages={requests.pagination?.totalPages}
            onPageChange={setPage}
            loading={isFetching}
          />
        }
        right={
          <FilterDock title="Filtros" onSearch={handleSearch}>
            <FilterGroup label="Estados">
              <FilterPills
                options={STATUS_OPTIONS}
                value={searchStatuses}
                onChange={setSearchStatuses}
              />
            </FilterGroup>
          </FilterDock>
        }
      />
    </Stack>
  );
};

export default FactoringRequests;
